import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Container, Form, Row, Col, Button } from "react-bootstrap";
import AlertOverlay from "../../../components/Alert";
import { useNavigate } from "react-router-dom";
import { useWarehouseClient } from "../../../apis/warehouseClient/WarehouseClientProvider";
import { getUiInterface, getPageName, getWordConnector, getErrorMessage, getSuccessMessage } from "../../../locales/translationUtils";
import { WarehouseClientVM } from "../../../services/warehouseClientService";
import { FaChevronDown } from "react-icons/fa";
import NavBar, { NavBarItem } from '../../../components/NavBar';
import { useGeneric } from '../../../apis/generic/GenericProvider';

const WarehouseClientCreateView: React.FC = () => {
    const { t } = useTranslation();
    const [alert, setAlert] = useState<{
        message: string;
        variant: "success" | "danger" | "warning" | "info";
    } | null>(null);
    const { postWarehouseClient } = useWarehouseClient();
    const navigate = useNavigate();
    const { distinctOptions, fetchDistinctOptions } = useGeneric();

    const [warehouseClient, setWarehouseClient] = useState<WarehouseClientVM>({
        version: 0,
        clientId: "",
        wrId: "",
    });

    useEffect(() => {
        fetchDistinctOptions('wareHouse');
        fetchDistinctOptions('client');
    }, []);

    const navItems: NavBarItem[] = [
        { label: t('warehouseClient.labels.home'), route: '/', iconName: "house-door" },
        { label: t('warehouseClient.labels.list'), route: "/WarehouseClient", iconName: "table" },
    ];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setWarehouseClient((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const nullRequiredFields: string[] = [];
        if (!warehouseClient.clientId) nullRequiredFields.push(t('warehouseClient.fields.client'));
        if (!warehouseClient.wrId) nullRequiredFields.push(t('warehouseClient.fields.wr'));

        if (nullRequiredFields.length > 0) {
            const warnMessage: string =
                nullRequiredFields.length === 1
                    ? getUiInterface("fieldRequiredSingle", {
                        field: nullRequiredFields[0],
                    })
                    : getUiInterface("fieldRequiredMultiple", {
                        fields: `${nullRequiredFields.slice(0, -1).join(", ")}${nullRequiredFields.length > 1
                            ? ` ${getWordConnector("and")} `
                            : ""
                            }${nullRequiredFields.slice(-1)}`,
                    });

            setAlert({ message: warnMessage, variant: "danger" });
            return;
        }

        try {
            const response = await postWarehouseClient(warehouseClient);

            if (response?.data?.id) {
                const successMessage = getSuccessMessage("createWarehouseClient", { id: response.data.id });
                navigate(`/WarehouseClient`, {
                    state: {
                        message: successMessage,
                        variant: "success",
                    },
                });
            } else {
                setAlert({
                    message: getErrorMessage("genericError", { error: response?.messages }),
                    variant: "danger",
                });
            }
        } catch (error) {
            setAlert({
                message: getErrorMessage("genericError", {}),
                variant: "danger",
            });
        }
    };

    return (
        <Container fluid>
            <NavBar items={navItems} />
            {alert && (
                <AlertOverlay
                    message={alert.message}
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                />
            )}

            <div className="mb-3"></div>
            <h2>{getPageName("createWarehouseClient")}</h2>
            <Form onSubmit={handleSubmit}>
                <Row>
                    <Col md={12} className="mb-3">
                        <Form.Group controlId="client">
                            <Form.Label>{t('warehouseClient.fields.client')}</Form.Label>
                            <div style={{ position: "relative", width: "20%" }}>
                                <Form.Control
                                    as="select"
                                    name="clientId"
                                    value={warehouseClient.clientId}
                                    onChange={handleChange}
                                >
                                    <option value="">{t('warehouseClient.labels.selectOption')}</option>
                                    {distinctOptions['client']?.map(
                                        (option: { id: number; description: string }) => (
                                            <option key={option.id} value={option.id.toString()}>
                                                {option.description}
                                            </option>
                                        )
                                    )}
                                </Form.Control>
                                <FaChevronDown
                                    style={{
                                        position: "absolute",
                                        right: "10px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        pointerEvents: "none",
                                    }}
                                />
                            </div>
                        </Form.Group>
                    </Col>

                    <Col md={12} className="mb-3">
                        <Form.Group controlId="wr">
                            <Form.Label>{t('warehouseClient.fields.wr')}</Form.Label>
                            <div style={{ position: "relative", width: "20%" }}>
                                <Form.Control
                                    as="select"
                                    name="wrId"
                                    value={warehouseClient.wrId}
                                    onChange={handleChange}
                                >
                                    <option value="">{t('warehouseClient.labels.selectOption')}</option>
                                    {distinctOptions['wareHouse']?.map(
                                        (option: { id: number; description: string }) => (
                                            <option key={option.id} value={option.id.toString()}>
                                                {option.description}
                                            </option>
                                        )
                                    )}
                                </Form.Control>
                                <FaChevronDown
                                    style={{
                                        position: "absolute",
                                        right: "10px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        pointerEvents: "none",
                                    }}
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>

                <Button className="mt-3 mb-3 d-flex gap-2" variant="primary" type="submit">
                    {getUiInterface("create")}
                </Button>
            </Form>
        </Container>
    );
};

export default WarehouseClientCreateView;
